import React, { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react';

import { useAuthContext } from '../components/layout/providers/AuthProvider';
import { UserAddress, getUserAddresses } from '../lib/api/addresses';

// DEBUG: Extend window interface for tracking
declare global {
  interface Window {
    addressFetchCount?: number;
    cartFetchCount?: number;
  }
}

interface AddressContextType {
  selectedAddress: UserAddress | null;
  addresses: UserAddress[];
  loading: boolean;
  error: string | null;
  setSelectedAddress: (address: UserAddress | null) => void;
  refreshAddresses: () => Promise<void>;
  loadIpLocation: () => Promise<void>;
  // Modal management for shared address dialogs
  showAddAddressDialog: boolean;
  setShowAddAddressDialog: (show: boolean) => void;
  editingAddress: UserAddress | null;
  setEditingAddress: (address: UserAddress | null) => void;
}

const AddressContext = createContext<AddressContextType | undefined>(undefined);

interface AddressProviderProps {
  children: ReactNode;
}

export function AddressProvider({ children }: AddressProviderProps) {
  // CRITICAL FIX: Track component mounts with unique ID for debugging
  const mountId = React.useRef(Math.random().toString(36).substr(2, 9));
  if (process.env.NODE_ENV === 'development') {
    console.log(`[AddressContext] PROVIDER MOUNT ${mountId.current}`, {
      timestamp: Date.now(),
      stack: new Error().stack?.split('\n')[2]?.trim()
    });
  }

  const { user, isLoading: authLoading } = useAuthContext();
  const [selectedAddress, setSelectedAddressState] = useState<UserAddress | null>(null);
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastUserIdRef = React.useRef<string | null>(null);
  const loadingRef = React.useRef(false); // Prevent concurrent address loads
  const apiCalledRef = React.useRef(false); // Prevent duplicate API calls

  // Modal management state
  const [showAddAddressDialog, setShowAddAddressDialog] = useState(false);
  const [editingAddress, setEditingAddress] = useState<UserAddress | null>(null);

  // Load addresses for authenticated users with timeout protection
  const loadAddresses = async () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[AddressContext] loadAddresses called, user present:', !!user, 'user role:', user?.role);
      // DEBUG: Track API call frequency
      window.addressFetchCount = (window.addressFetchCount || 0) + 1;
      console.log('[AddressContext] ADDRESS FETCH COUNT:', window.addressFetchCount);
    }

    // Prevent concurrent address loads (rate limiting fix)
    if (loadingRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[AddressContext] Address load already in progress, skipping');
      }
      return;
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      if (user) {
        // FAANG-GRADE: Role-based context loading - only customers should load addresses
        if (user.role !== 'customer') {
          if (process.env.NODE_ENV === 'development') {
            console.log('[AddressContext] Skipping address load - user role is:', user.role, 'not customer');
          }
          setAddresses([]);
          setSelectedAddressState(null);
          setLoading(false);
          return;
        }

        if (process.env.NODE_ENV === 'development') {
          console.log('[AddressContext] User is customer, loading user addresses');
        }

        // Load addresses without timeout - API should respond quickly
        const response = await getUserAddresses() as any;

        const userAddresses = response.addresses || [];
        setAddresses(userAddresses);

        // Try to restore previously selected address
        const persistedAddressId = localStorage.getItem('selectedAddressId');
        if (persistedAddressId) {
          const persistedAddress = userAddresses.find(addr => addr.id === persistedAddressId);
          if (persistedAddress) {
            setSelectedAddressState(persistedAddress);
            return;
          }
        }

        // If no persisted address, select default or first address
        const defaultAddr = userAddresses.find(addr => addr.is_default) || userAddresses[0];
        if (defaultAddr) {
          setSelectedAddressState(defaultAddr);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('[AddressContext] No user (anonymous), clearing addresses');
        }
        // For anonymous users, clear addresses - no IP geolocation
        setAddresses([]);
        setSelectedAddressState(null);
      }
    } catch (err) {
      console.warn('[AddressContext] Failed to load addresses:', err);
      // Don't set error for anonymous users - IP location will handle it
      if (user) {
        setError('Failed to load addresses. You can still browse services.');
      }
      // Ensure we have usable state even on error
      if (!user) {
        setAddresses([]);
        setSelectedAddressState(null);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  };


  // Set selected address with persistence
  const setSelectedAddress = React.useCallback((address: UserAddress | null) => {
    setSelectedAddressState(address);

    // Persist for authenticated users
    if (user && address?.id) {
      localStorage.setItem('selectedAddressId', address.id);
    } else if (user && !address) {
      localStorage.removeItem('selectedAddressId');
    }

    // Dispatch event asynchronously to avoid state update conflicts
    if (typeof window !== 'undefined') {
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('addressChanged', { detail: { address } }));
      }, 0);
    }
  }, [user]);

  // Refresh addresses (useful after creating/editing addresses)
  const refreshAddresses = async () => {
    await loadAddresses();
  };

  // CRITICAL FIX: Load addresses on mount and when authentication changes
  // Prevent duplicate API calls with apiCalledRef
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[AddressContext] useEffect triggered ${mountId.current}`, {
        authLoading,
        userId: user?.id,
        userRole: user?.role,
        lastUserId: lastUserIdRef.current,
        apiCalled: apiCalledRef.current
      });
    }

    // Wait for auth to finish loading
    if (authLoading) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[AddressContext] Auth still loading ${mountId.current}, skipping address load`);
      }
      return;
    }

    // Prevent duplicate API calls for the same user session
    if (apiCalledRef.current && user && user.id === lastUserIdRef.current) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[AddressContext] API already called for this user ${mountId.current}, skipping`);
      }
      return;
    }

    // Update the ref to track the current user
    lastUserIdRef.current = user?.id || null;

    // Stagger address loading to prevent simultaneous API calls with other contexts
    const timeoutId = setTimeout(() => {
      const loadAddressesAsync = async () => {
        try {
          // Mark API as called before making the request
          apiCalledRef.current = true;
          await loadAddresses();
        } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.error(`[AddressContext] Critical error in address loading ${mountId.current}:`, err);
          }
          // Reset API called flag on error so it can retry
          apiCalledRef.current = false;
          // Ensure we have usable state even on critical errors
          setLoading(false);
          setAddresses([]);
          setSelectedAddressState(null);
          if (user) {
            setError('Address loading failed. You can still browse services.');
          }
        }
      };

      loadAddressesAsync();
    }, 100); // Delay to stagger with CartContext (which uses 150ms)

    return () => clearTimeout(timeoutId);
  }, [user?.id, authLoading]); // Include authLoading in dependencies

  const value: AddressContextType = useMemo(() => ({
    selectedAddress,
    addresses,
    loading,
    error,
    setSelectedAddress,
    refreshAddresses,
    loadIpLocation: async () => {
      console.warn('[AddressContext] IP location loading removed - no longer supported');
    },
    showAddAddressDialog,
    setShowAddAddressDialog,
    editingAddress,
    setEditingAddress,
  }), [selectedAddress, addresses, loading, error, setSelectedAddress, refreshAddresses, showAddAddressDialog, editingAddress]);

  return (
    <AddressContext.Provider value={value}>
      {children}
    </AddressContext.Provider>
  );
}

export function useAddress(): AddressContextType {
  const context = useContext(AddressContext);
  if (context === undefined) {
    // Return safe default values when provider is not available
    // This prevents crashes and allows components to render gracefully
    console.warn('[AddressContext] useAddress called outside of AddressProvider, using defaults');
    return {
      selectedAddress: null,
      addresses: [],
      loading: false,
      error: 'Address service temporarily unavailable',
      setSelectedAddress: (address) => {
        console.warn('[AddressContext] setSelectedAddress called but no provider available');
      },
      refreshAddresses: async () => {
        console.warn('[AddressContext] refreshAddresses called but no provider available');
      },
      loadIpLocation: async () => {
        console.warn('[AddressContext] IP location loading removed - no longer supported');
      },
      showAddAddressDialog: false,
      setShowAddAddressDialog: (show) => {
        console.warn('[AddressContext] setShowAddAddressDialog called but no provider available');
      },
      editingAddress: null,
      setEditingAddress: (address) => {
        console.warn('[AddressContext] setEditingAddress called but no provider available');
      },
    };
  }
  return context;
}